<template>
  <div class="beloit-container relative min-h-screen pb-20 overflow-hidden">
    <!-- Background Elements -->
    <div class="absolute inset-0 bg-river-pattern opacity-10"></div>
    <div class="absolute top-0 right-0 w-1/2 h-1/3 bg-gradient-radial from-blue-500/10 to-transparent opacity-30 blur-3xl"></div>
    <div class="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-radial from-primary/15 to-transparent opacity-25 blur-3xl"></div>
    
    <!-- River Flow Elements -->
    <div class="river-flow-top"></div>
    <div class="river-flow-bottom"></div>
    
    <!-- Floating Attraction Elements -->
    <div class="floating-attractions">
      <div 
        v-for="(attraction, index) in floatingAttractions" 
        :key="index"
        class="floating-attraction"
        :class="`attraction-${index + 1}`"
        :style="{ backgroundImage: `url(${attraction.src})` }"
        @mousemove="handleAttractionTilt($event, index)"
        @mouseleave="resetAttractionTilt(index)"
      >
        <div class="attraction-border"></div>
        <div class="attraction-overlay">
          <span class="attraction-label">{{ attraction.label }}</span>
        </div>
      </div>
    </div>

    <!-- Decorative River Elements -->
    <div class="absolute left-0 top-1/3 h-[50%] w-2 bg-gradient-to-b from-transparent via-blue-400/20 to-transparent transform -skew-y-12"></div>
    <div class="absolute right-0 top-1/4 h-[60%] w-2 bg-gradient-to-b from-transparent via-blue-300/15 to-transparent transform skew-y-12"></div>
    
    <!-- Water Accent Elements -->
    <div class="water-accent top-accent"></div>
    <div class="water-accent bottom-accent"></div>
    <div class="water-accent left-flow"></div>
    <div class="water-accent right-flow"></div>
    <!-- Header Section with River Parallax -->
    <div 
      class="beloit-header relative z-10 pt-[12vh] px-6 text-center mb-16 opacity-0 transform translate-y-8"
      ref="header"
      @mousemove="handleRiverParallax($event, 'header')"
      :class="{'fade-in': isInView.header}"
    >
      <div class="header-river-line"></div>
      <div class="header-ripple-circle"></div>
      
      <h2 class="text-blue-300/80 text-2xl md:text-3xl mb-3 font-light tracking-wide">{{ firstLineText }}</h2>
      <h1 class="text-white text-5xl md:text-7xl font-bold relative inline-block mb-4">
        <span class="relative z-10">{{ secondLineText }}</span>
        <span class="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-primary transform scale-x-0 origin-left transition-transform duration-1000"
              :class="{'scale-x-100': isInView.header}"></span>
      </h1>
      <div class="mt-6 max-w-4xl mx-auto text-center">
        <p class="text-white/75 text-xl md:text-2xl leading-relaxed font-light mb-4">
          {{ taglineText }}
        </p>
        <p class="text-blue-200/60 text-lg md:text-xl leading-relaxed">
          Where industrial heritage meets modern innovation along the beautiful Rock River.
        </p>
      </div>
    </div>
    
    <!-- Content Container -->
    <div class="content-wrapper relative max-w-7xl mx-auto px-4 mt-12">
      <!-- Main Content Grid -->
      <div class="content-grid">
        <!-- Heritage & Culture Section -->
        <div class="content-section heritage-section" ref="section1" :class="{'fade-in': isInView.section1}">
          <div class="content-card river-glassmorphism">
            <div class="card-header">
              <div class="section-icon">
                <font-awesome-icon :icon="['fas', 'landmark']" />
              </div>
              <h3 class="card-title">Heritage & Culture</h3>
              <div class="river-accent-line"></div>
            </div>
            
            <p class="text-blue-200/90 text-xl md:text-2xl font-light leading-tight mb-6">
              Beloit is an alloy of strength, beauty, and diversity all melded together into a community.
            </p>
            
            <p class="text-white/85 text-base md:text-lg leading-relaxed mb-4">
              Located on the banks of the Rock River at the Wisconsin-Illinois border, Beloit has evolved from its industrial roots into a vibrant cultural destination. Home to Wisconsin's oldest college, founded in 1846, the city seamlessly blends historic charm with modern innovation.
            </p>
            
            <p class="text-white/85 text-base md:text-lg leading-relaxed mb-6">
              The downtown has undergone remarkable revitalization, featuring boutique hotels like the Ironworks Hotel, diverse dining from farm-to-table cuisine to modern Italian at Velvet Buffalo, and a thriving arts scene centered around the Beloit Art Center and numerous galleries.
            </p>
            <!-- Image Placeholder 1 -->
            <div class="image-display river-glassmorphism mb-6" ref="imageDisplay1">
              <img src="/img/beloit/beloit-campus.jpg" alt="Beloit College Campus - Wisconsin's Oldest College Founded 1846" class="display-image" />
              <div class="image-caption">
                <p class="caption-text">Beloit College Campus</p>
                <p class="caption-subtext">Wisconsin's Oldest College - Founded 1846</p>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Modern Development Section -->
        <div class="content-section development-section" ref="section2" :class="{'fade-in': isInView.section2}">
          <div class="content-card river-glassmorphism">
            <div class="card-header">
              <div class="section-icon">
                <font-awesome-icon :icon="['fas', 'city']" />
              </div>
              <h3 class="card-title">Modern Development</h3>
              <div class="river-accent-line"></div>
            </div>
            
            <p class="text-blue-200/90 text-xl md:text-2xl font-light leading-tight mb-6">
              Downtown revitalization has transformed Beloit into a perfect live, work, learn and play destination.
            </p>
            
            <p class="text-white/85 text-base md:text-lg leading-relaxed mb-4">
              The Ironworks Campus anchors the downtown renaissance, housing innovative businesses alongside the luxury Ironworks Hotel. New developments like Henry Dorrbaker's entertainment venue and Wright & Wagner Lofts create exciting urban living options.
            </p>

            <!-- Attractions Grid -->
            <div class="attractions-grid mb-6">
              <div class="attraction-item">
                <div class="attraction-icon">
                  <font-awesome-icon :icon="['fas', 'water']" />
                </div>
                <span>Rock River Trail</span>
              </div>
              <div class="attraction-item">
                <div class="attraction-icon">
                  <font-awesome-icon :icon="['fas', 'baseball']" />
                </div>
                <span>Beloit Sky Carp</span>
              </div>
              <div class="attraction-item">
                <div class="attraction-icon">
                  <font-awesome-icon :icon="['fas', 'palette']" />
                </div>
                <span>Arts District</span>
              </div>
              <div class="attraction-item">
                <div class="attraction-icon">
                  <font-awesome-icon :icon="['fas', 'seedling']" />
                </div>
                <span>Farmers Market</span>
              </div>
            </div>
            <!-- Image Display 2 -->
            <div class="image-display river-glassmorphism mb-6" ref="imageDisplay2">
              <img src="/img/beloit/farmers-market.jpg" alt="Beloit Farmers Market - Downtown Community Events" class="display-image" />
              <div class="image-caption">
                <p class="caption-text">Downtown Revitalization</p>
                <p class="caption-subtext">Farmers Market & Community Events</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Community Experience Section -->
      <div class="community-section" ref="section3" :class="{'fade-in': isInView.section3}">
        <div class="community-card river-glassmorphism">
          <div class="community-content">
            <div class="card-header center-header">
              <div class="section-icon">
                <font-awesome-icon :icon="['fas', 'heart']" />
              </div>
              <h3 class="card-title">The Beloit Experience</h3>
              <div class="river-accent-line center"></div>
            </div>
            
            <p class="text-blue-200/90 text-xl md:text-2xl font-light leading-tight mb-6 text-center">
              Experience the perfect blend of history, culture, and outdoor adventures in Wisconsin's Gateway City.
            </p>
            
            <div class="experience-grid">
              <div class="experience-item">
                <div class="experience-icon">
                  <font-awesome-icon :icon="['fas', 'utensils']" />
                </div>
                <h4>Culinary Scene</h4>
                <p>From Merrill & Houston's Steak Joint to Lucy's #7 Burger Bar, discover Wisconsin's emerging foodie destination.</p>
              </div>
              
              <div class="experience-item">
                <div class="experience-icon">
                  <font-awesome-icon :icon="['fas', 'music']" />
                </div>
                <h4>Arts & Entertainment</h4>
                <p>Over 50 annual events, galleries, museums, and live entertainment create a vibrant cultural atmosphere.</p>
              </div>
              
              <div class="experience-item">
                <div class="experience-icon">
                  <font-awesome-icon :icon="['fas', 'tree']" />
                </div>
                <h4>Outdoor Recreation</h4>
                <p>Rock River Water Trail, Riverside Park, and year-round outdoor activities for all ages and interests.</p>
              </div>
            </div>
            <!-- Image Display 3 -->
            <div class="image-display river-glassmorphism mb-8" ref="imageDisplay3">
              <img src="/img/beloit/river-recreation.jpg" alt="Rock River Recreation - Outdoor Activities and Natural Beauty" class="display-image" />
              <div class="image-caption">
                <p class="caption-text">Rock River & Recreation</p>
                <p class="caption-subtext">Outdoor Activities & Natural Beauty</p>
              </div>
            </div>
            
            <!-- Call to Action -->
            <div class="beloit-cta">
              <a 
                href="https://www.thisisbeloit.com/"
                target="_blank"
                rel="noopener noreferrer"
                class="beloit-button group"
                @mouseenter="btnHovered = true"
                @mouseleave="btnHovered = false"
              >
                <span class="btn-text" :class="{'btn-text-animate': btnHovered}">
                  Explore Beloit
                </span>
                <span class="btn-icon" :class="{'btn-icon-animate': btnHovered}">
                  <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                </span>
                <div class="btn-bg" :class="{'btn-bg-animate': btnHovered}"></div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- River Separator -->
    <div class="river-separator">
      <div class="river-flow">
        <div class="flow-line left"></div>
        <div class="river-symbol">
          <div class="symbol-outer"></div>
          <div class="symbol-inner">
            <div class="river-waves"></div>
          </div>
        </div>
        <div class="flow-line right"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ThisIsBeloit',
  data() {
    return {
      firstLineText: 'Gateway to Wisconsin',
      secondLineText: 'This is Beloit',
      taglineText: 'An alloy of strength, beauty, and diversity all melded together into a community.',
      observers: [],
      isInView: {
        header: false,
        section1: false,
        section2: false,
        section3: false
      },
      btnHovered: false,
      floatingAttractions: [
        { src: '/img/beloit/ironworks.jpg', tilt: { x: 0, y: 0 }, label: 'Ironworks Hotel' },
        { src: '/img/beloit/college.jpg', tilt: { x: 0, y: 0 }, label: 'Beloit College' },
        { src: '/img/beloit/downtown.jpg', tilt: { x: 0, y: 0 }, label: 'Downtown Arts' },
        { src: '/img/beloit/culinary.jpg', tilt: { x: 0, y: 0 }, label: 'Culinary Scene' },
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setupIntersectionObservers();
      
      // Add initial entrance animation
      setTimeout(() => {
        this.isInView.header = true;
      }, 300);
    });
  },
  beforeUnmount() {
    this.observers.forEach(observer => observer.disconnect());
  },  methods: {
    setupIntersectionObservers() {
      const sections = [
        { ref: 'header', threshold: 0.3 },
        { ref: 'section1', threshold: 0.2 },
        { ref: 'section2', threshold: 0.2 },
        { ref: 'section3', threshold: 0.2 }
      ];
      
      sections.forEach(section => {
        const element = this.$refs[section.ref];
        if (!element) return;
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.isInView[section.ref] = true;
            } else if (section.ref !== 'header') {
              this.isInView[section.ref] = false;
            }
          });
        }, { 
          threshold: section.threshold,
          rootMargin: '0px 0px -100px 0px'
        });
        
        observer.observe(element);
        this.observers.push(observer);
      });
    },

    handleRiverParallax(e, elementType) {
      const element = this.$refs[elementType];
      if (!element) return;
      
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const distanceX = (e.clientX - centerX) / (rect.width / 2);
      const distanceY = (e.clientY - centerY) / (rect.height / 2);
      
      if (elementType === 'header') {
        element.style.transform = `translate(${distanceX * -8}px, ${distanceY * -8}px)`;
      }
    },    
    handleAttractionTilt(e, index) {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();
      
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      
      const tiltX = y * -4;
      const tiltY = x * 4;
      
      this.floatingAttractions[index].tilt = { x: tiltX, y: tiltY };
      
      target.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.03, 1.03, 1.03)`;
      
      const border = target.querySelector('.attraction-border');
      if (border) {
        border.style.transform = `translate(${x * -3}px, ${y * -3}px)`;
      }
    },
    
    resetAttractionTilt(index) {
      const attraction = document.querySelector(`.attraction-${index + 1}`);
      if (attraction) {
        attraction.style.transform = '';
        
        const border = attraction.querySelector('.attraction-border');
        if (border) {
          border.style.transform = '';
        }
      }
      
      this.floatingAttractions[index].tilt = { x: 0, y: 0 };
    }
  }
};
</script>
<style scoped>
/* Base Styles */
.beloit-container {
  color: white;
  will-change: transform;
  background: linear-gradient(135deg, #1a1f2e 0%, #2d3748 50%, #1a1f2e 100%);
}

.bg-river-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20v20h20v-20c11.046 0 20-8.954 20-20s-8.954-20-20-20z'/%3E%3C/g%3E%3C/svg%3E");
}

/* River Flow Elements */
.river-flow-top,
.river-flow-bottom {
  position: absolute;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.4), transparent);
  z-index: 2;
}

.river-flow-top {
  top: 20%;
  animation: flow-right 15s linear infinite;
}

.river-flow-bottom {
  bottom: 25%;
  animation: flow-left 20s linear infinite;
}

/* Floating Attractions Styling - Updated for better text visibility */
.floating-attractions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 11;
  pointer-events: none;
}

.floating-attraction {
  position: absolute;
  width: 280px;
  height: 200px;
  background-size: cover;
  background-position: center;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  opacity: 0.3; /* Much more transparent when not hovered */
  transition: all 0.3s ease;
  cursor: pointer;
  pointer-events: auto;
  transform-style: preserve-3d;
  overflow: hidden;
  z-index: 1; /* Lower z-index by default */
}

.floating-attraction:hover {
  opacity: 1;
  z-index: 25; /* High z-index on hover so it can cover text */
  transform: scale(1.05);
}
.attraction-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(96, 165, 250, 0.6);
  border-radius: 16px;
  box-sizing: border-box;
  transition: transform 0.3s ease;
}

.attraction-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 12px;
  border-radius: 0 0 16px 16px;
  opacity: 0; /* Hidden by default */
  transition: opacity 0.3s ease;
}

.floating-attraction:hover .attraction-overlay {
  opacity: 1; /* Show only on hover */
}

.attraction-label {
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

/* Position floating attractions */
.attraction-1 {
  top: 18%;
  right: 80px;
  transform: rotate(-2deg);
  z-index: 8;
}

.attraction-2 {
  top: 35%;
  left: 60px;
  transform: rotate(3deg);
  z-index: 7;
}

.attraction-3 {
  top: 55%;
  right: 100px;
  transform: rotate(-4deg);
  z-index: 8;
}

.attraction-4 {
  top: 75%;
  left: 90px;
  transform: rotate(2deg);
  z-index: 7;
}
/* Water Accent Elements */
.water-accent {
  position: absolute;
  opacity: 0.15;
  z-index: 1;
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.6), rgba(147, 197, 253, 0.3));
}

.top-accent {
  top: 0;
  left: 15%;
  width: 25%;
  height: 2px;
  transform: skewX(-20deg);
}

.bottom-accent {
  bottom: 0;
  right: 15%;
  width: 25%;
  height: 2px;
  transform: skewX(20deg);
}

.left-flow {
  top: 25%;
  left: 0;
  width: 30px;
  height: 50%;
  clip-path: polygon(0 0, 100% 30%, 100% 70%, 0 100%);
}

.right-flow {
  top: 30%;
  right: 0;
  width: 30px;
  height: 40%;
  clip-path: polygon(0 30%, 100% 0, 100% 100%, 0 70%);
}

/* Header Styles */
.beloit-header {
  transition: opacity 1s ease, transform 1s ease;
  position: relative;
  z-index: 10; /* Ensure header is above floating attractions */
}

.header-river-line {
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, rgba(96, 165, 250, 0.8), transparent);
  margin: 0 auto 1.5rem;
  border-radius: 2px;
}

.header-ripple-circle {
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  border: 1px solid rgba(96, 165, 250, 0.2);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.4;
  animation: ripple 25s linear infinite;
  pointer-events: none;
}
.header-ripple-circle::before {
  content: '';
  position: absolute;
  width: 350px;
  height: 350px;
  border-radius: 50%;
  border: 1px dashed rgba(96, 165, 250, 0.1);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: ripple 35s linear infinite reverse;
}

/* Content Styles - Updated for better text visibility */
.content-wrapper {
  position: relative;
  z-index: 10; /* Higher z-index so content appears above floating attractions */
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  margin-bottom: 100px;
}

.content-section {
  opacity: 0;
  transform: translateY(50px);
  transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  z-index: 10; /* Ensure content is above floating attractions */
}

.content-section.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.content-card {
  padding: 50px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  z-index: 10; /* Ensure cards are above floating attractions */
}

.river-glassmorphism {
  background: rgba(96, 165, 250, 0.05);
  border: 1px solid rgba(96, 165, 250, 0.15);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.river-glassmorphism:hover {
  background: rgba(96, 165, 250, 0.08);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
  border-color: rgba(96, 165, 250, 0.25);
}
.card-header {
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.center-header {
  justify-content: center;
  flex-direction: column;
  gap: 0.5rem;
}

.section-icon {
  color: rgba(96, 165, 250, 0.8);
  font-size: 2rem;
  min-width: 40px;
}

.card-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  color: white;
}

.river-accent-line {
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, rgba(96, 165, 250, 0.8), rgba(147, 197, 253, 0.4));
  transition: width 0.3s ease;
  border-radius: 2px;
}

.river-accent-line.center {
  margin: 0 auto;
}

.content-card:hover .river-accent-line {
  width: 140px;
}

/* Attractions Grid */
.attractions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  border-top: 1px solid rgba(96, 165, 250, 0.2);
  padding-top: 1.5rem;
}

.attraction-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(96, 165, 250, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(96, 165, 250, 0.1);
  transition: all 0.3s ease;
}
.attraction-item:hover {
  background: rgba(96, 165, 250, 0.1);
  transform: translateY(-3px);
  border-color: rgba(96, 165, 250, 0.3);
}

.attraction-icon {
  color: rgba(96, 165, 250, 0.9);
  font-size: 1.5rem;
  min-width: 24px;
}

/* Experience Grid */
.experience-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.experience-item {
  text-align: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 16px;
  border: 1px solid rgba(96, 165, 250, 0.1);
  transition: all 0.3s ease;
}

.experience-item:hover {
  background: rgba(96, 165, 250, 0.05);
  transform: translateY(-5px);
  border-color: rgba(96, 165, 250, 0.2);
}

.experience-icon {
  color: rgba(96, 165, 250, 0.8);
  font-size: 3rem;
  margin-bottom: 1rem;
}

.experience-item h4 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: white;
}

.experience-item p {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
}

/* Community Section */
.community-section {
  opacity: 0;
  transform: translateY(50px);
  transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
  margin-bottom: 4rem;
  position: relative;
  z-index: 10; /* Ensure community section is above floating attractions */
}

.community-section.fade-in {
  opacity: 1;
  transform: translateY(0);
}

.community-card {
  padding: 60px 50px;
  text-align: center;
  position: relative;
  z-index: 10; /* Ensure card is above floating attractions */
}

/* Image Display Styles */
.image-display {
  height: 220px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.image-display:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.display-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.image-display:hover .display-image {
  transform: scale(1.05);
}

.image-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 16px;
  color: white;
}

.caption-text {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.caption-subtext {
  font-size: 0.875rem;
  opacity: 0.9;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Button Styling */
.beloit-cta {
  text-align: center;
  margin-top: 2rem;
}

.beloit-button {
  position: relative;
  padding: 18px 36px;
  border-radius: 12px;
  font-weight: bold;
  color: white;
  overflow: hidden;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  background: transparent;
  border: 2px solid rgba(96, 165, 250, 0.6);
  transition: all 0.3s ease;
  font-size: 1.25rem;
}
.btn-text {
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease;
}

.btn-text-animate {
  transform: translateX(-6px);
}

.btn-icon {
  position: relative;
  z-index: 2;
  opacity: 0.8;
  transition: all 0.3s ease;
  font-size: 1.1rem;
}

.btn-icon-animate {
  transform: translateX(6px);
  opacity: 1;
}

.btn-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, rgba(96, 165, 250, 0.8), rgba(59, 130, 246, 0.9));
  z-index: 1;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.btn-bg-animate {
  transform: scaleX(1);
}

.beloit-button:hover {
  box-shadow: 0 10px 30px rgba(96, 165, 250, 0.3);
  border-color: rgba(96, 165, 250, 0.9);
  transform: translateY(-3px);
}

/* River Separator */
.river-separator {
  position: absolute;
  bottom: -120px;
  left: 50%;
  transform: translateX(-50%);
  width: 350px;
  height: 80px;
  z-index: 10;
}

.river-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.flow-line {
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.6), transparent);
  flex: 1;
}

.river-symbol {
  width: 50px;
  height: 50px;
  position: relative;
  margin: 0 25px;
}

.symbol-outer {
  width: 100%;
  height: 100%;
  border: 2px solid rgba(96, 165, 250, 0.4);
  border-radius: 50%;
  position: absolute;
  animation: ripple 18s linear infinite;
}

.symbol-inner {
  width: 30px;
  height: 30px;
  background: rgba(96, 165, 250, 0.2);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.river-waves::before {
  content: '〜';
  color: rgba(96, 165, 250, 0.8);
  font-size: 16px;
  animation: wave-pulse 3s ease-in-out infinite;
}

/* Animations */
.fade-in {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

@keyframes flow-right {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100vw); }
}

@keyframes flow-left {
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100vw); }
}

@keyframes ripple {
  from { transform: translate(-50%, -50%) rotate(0deg) scale(1); }
  to { transform: translate(-50%, -50%) rotate(360deg) scale(1.1); }
}
@keyframes wave-pulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
}

/* Responsive Styles */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
    gap: 60px;
  }
  
  .floating-attractions {
    display: none;
  }
  
  .attractions-grid {
    grid-template-columns: 1fr;
  }
  
  .experience-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .beloit-header {
    padding-top: 8vh;
  }
  
  .card-title {
    font-size: 1.5rem;
  }
  
  .content-card,
  .community-card {
    padding: 40px 30px;
  }
  
  .image-placeholder {
    height: 180px;
  }
  
  .placeholder-icon {
    font-size: 2.5rem;
  }
  
  .beloit-button {
    padding: 14px 28px;
    font-size: 1.125rem;
  }
}

@media (max-width: 640px) {
  .beloit-header {
    padding-top: 6vh;
  }
  
  .content-card,
  .community-card {
    padding: 30px 20px;
  }
  
  .experience-item {
    padding: 1.5rem;
  }
}

/* CSS Variables for colors */
:root {
  --color-primary: #a22a21;
  --color-secondary: #b7b9ba;
  --color-river: #60a5fa;
}
</style>